import { useQuery, useReactiveVar } from "@apollo/client";
import { Link } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

import CloudinaryImage from "@/components/cloudinary-image";
import MarkerIcon from "@/components/icons/marker-icon";
import { Card, CardContent } from "@/components/ui/card";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import useAuth from "@/hooks/use-auth";
import { user as userStore } from "@/store";

import { type DataType, QUERY, type VariablesType } from "./graphql";

interface ProfileCardProps {
  children?: React.ReactNode;
  userId: string;
}

interface StatCountProps {
  count?: number;
  label: string;
}

function ProfileCardContent({ userId }: ProfileCardProps) {
  const { isUser, queryContext } = useAuth();

  const { data } = useQuery<DataType, VariablesType>(
    QUERY,
    queryContext({ isUser, userId })
  );

  const user = useMemo(() => data?.user, [data]);

  const image = useMemo(
    () =>
      user ? (
        <CloudinaryImage
          className="size-17.5 rounded-full"
          rW={150}
          size={70}
          user={user}
        />
      ) : null,
    [user]
  );

  const MutualFriends = useCallback(() => {
    if (!user?.mutualFriends) {
      return null;
    }

    return (
      <div className="ml-auto flex shrink-0 self-start">
        {user.mutualFriends.objects.map((friend, index) => (
          <CloudinaryImage
            className={`${index > 0 ? "-ml-2.5" : ""} size-5 self-center overflow-hidden rounded-full border-0.5 border-eerie-black`}
            key={friend.username}
            rW={40}
            size={20}
            user={friend}
          />
        ))}
        <p className="ml-1 self-center font-medium text-12 text-white">
          {user.mutualFriends.count} mutual
          {user.mutualFriends.count !== 1 && "s"}
        </p>
      </div>
    );
  }, [user]);

  const statCategories = [
    {
      count: user?.experiencesCount,
      label: "experience",
    },
    {
      count: user?.followersCount,
      label: "follower",
    },
  ];

  const StatCount = useCallback(({ count, label }: StatCountProps) => {
    return (
      <div className="flex h-8 grow-1 items-center justify-center gap-1.5 rounded-[5px] bg-white/10 font-medium text-14">
        <p className="text-white">{count}</p>
        <p className="text-slate-gray">{label + (count === 1 ? "" : "s")}</p>
      </div>
    );
  }, []);

  if (!user) {
    return null;
  }

  return (
    <Card className="w-md rounded-[10px] border-0 bg-eerie-black p-0 text-white shadow-none shadow-white/25 transition-shadow hover:shadow-0-0-20">
      <CardContent className="p-4">
        <div className="mb-4 flex gap-2">
          <div className="flex min-w-0 items-center">
            <Link className="mr-4" to={`/@${user.username}`}>
              {image}
            </Link>
            <div className="flex min-w-0 flex-col items-start gap-1">
              <div>
                <Link className="hover:underline" to={`/@${user.username}`}>
                  <span className="block truncate">{user.username}</span>
                </Link>
              </div>
              <p className="truncate text-14 text-slate-gray">
                {user.firstName} {user.lastName}
              </p>
              {user.location && (
                <p className="text-12 text-slate-gray">
                  <MarkerIcon className="-ml-0.5 size-[15px] text-slate-gray" />
                  {user.location}
                </p>
              )}
            </div>
          </div>
          <MutualFriends />
        </div>
        {user.bio && (
          <p className="mb-4 line-clamp-2 text-13/5 text-pale-lavender">
            {user.bio}
          </p>
        )}
        <div
          className="grid grid-cols-(--column-count) gap-4"
          style={
            {
              "--column-count": `repeat(${statCategories.length}, minmax(0, 1fr))`,
            } as React.CSSProperties
          }
        >
          {statCategories.map((stat) => (
            <StatCount count={stat.count} key={stat.label} label={stat.label} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ProfileCard({ userId, children }: ProfileCardProps) {
  const ownUserId = useReactiveVar(userStore)?.id || "";

  if (ownUserId === userId) {
    return <>{children}</>;
  }

  return (
    <HoverCard>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent className="hidden bg-transparent p-4 md:block">
        <ProfileCardContent userId={userId} />
      </HoverCardContent>
    </HoverCard>
  );
}
